<script setup lang="ts">
import { PropType, ref, useId, watch } from "vue";
import Icon from "@/components/Core/Icon.vue";
import { Notice } from "@/services/notices";

const props = defineProps({
  notice: { type: Object as PropType<Notice>, required: true },
  pending: { type: Boolean, default: false },
  failed: { type: Boolean, default: false },
});
defineEmits({ dismiss: () => true, activate: () => true });

const titleId = useId();
const imageFailed = ref(false);
watch(
  () => props.notice.imageUrl,
  () => {
    imageFailed.value = false;
  },
);
</script>

<template>
  <section
    :aria-labelledby="titleId"
    class="pointer-events-auto relative max-h-[calc(100dvh-var(--mobile-navbar-height,0px)-6.5rem)] overflow-y-auto rounded-xl bg-white shadow-xl ring-1 ring-gray-200 md:max-h-[calc(100dvh-8rem)]"
  >
    <div class="flex items-center justify-between px-4 pt-3 pb-2">
      <span
        class="text-xs font-semibold tracking-wide text-violet-700 uppercase"
      >
        What's new
      </span>
      <button
        type="button"
        aria-label="Dismiss announcement"
        :aria-busy="pending"
        class="-mr-1 rounded-md p-2 text-gray-600 hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-700 aria-busy:opacity-50"
        @click="$emit('dismiss')"
      >
        <Icon name="times" class="size-4" aria-hidden="true" />
      </button>
    </div>
    <a
      :href="notice.linkUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="group block px-4 pb-4 text-gray-700 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-violet-700"
      @click="$emit('activate')"
    >
      <img
        v-if="notice.imageUrl && !imageFailed"
        :src="notice.imageUrl"
        alt=""
        class="mb-3 aspect-video w-full rounded-lg object-cover"
        @error="imageFailed = true"
      />
      <h2
        :id="titleId"
        class="text-base leading-snug font-semibold break-words text-gray-900"
      >
        {{ notice.title }}
      </h2>
      <p class="mt-2 text-sm leading-relaxed break-words whitespace-pre-line">
        {{ notice.content }}
      </p>
      <span
        class="mt-3 inline-block text-sm font-semibold text-violet-700 group-hover:underline"
      >
        Learn more<span class="sr-only"> (opens in a new tab)</span>
      </span>
    </a>
    <p v-if="failed" role="status" class="px-4 pb-4 text-sm text-red-700">
      Couldn't save your dismissal. Please try again.
    </p>
  </section>
</template>
